import { useContext, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCamera,
  faSave,
  faCrown,
  faLock,
  faPaw,
  faPhone,
  faTaxi,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import api from "../../api/api";
import { Link } from "react-router-dom";
import { routes } from "../../config/utility";
import { differenceInDays } from "date-fns";
import AccountDeletionSection from "../../components/AccountDeletionSection";

const UserProfile = () => {
  const { user, updateContext } = useContext(UserContext);
  const [userData, setUserData] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    emergency_contact: user?.emergency_contact || "",
    profilePhoto: user?.profile_photo || "https://via.placeholder.com/150",
    walletBalance: user?.wallet_balance || 0,
    membership: user?.membership || null,
    permission: user?.permission || null,
    autoRenew: user?.membership?.auto_renew ? 1 : 0,
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [newPhoto, setNewPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const daysLeft = userData.membership
    ? differenceInDays(new Date(userData.membership.end_date), new Date())
    : null;

  // Handle Photo Change
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("profile_photo", file);
      formData.append("_method", "PUT");

      try {
        setLoading(true);
        const response = await api.post(`/user/update`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        setNewPhoto(URL.createObjectURL(file));
        setUserData((prevData) => ({
          ...prevData,
          profilePhoto: response.data.profile_photo,
        }));

        updateContext();
        toast.success("Profile photo updated!");
      } catch (error) {
        toast.error("Failed to update profile photo.");
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle Name Change
  const handleNameChange = (e) => {
    setUserData((prevData) => ({
      ...prevData,
      name: e.target.value,
    }));
  };

  // Handle Phone Change
  const handlePhoneChange = (phone) => {
    setUserData((prevData) => ({
      ...prevData,
      phone,
    }));
  };

  const handleEPhoneChange = (emergency_contact) => {
    setUserData((prevData) => ({
      ...prevData,
      emergency_contact,
    }));
  };

  const handlePasswordChange = (e) => {
    setPasswordData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value,
    }));
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordData;

    if (newPassword !== confirmPassword) {
      toast.error("New Password and Confirm Password do not match!");
      return;
    }

    try {
      await api.post("/user/update", {
        _method: "PUT",
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });

      toast.success("Password changed successfully!");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      toast.error("Failed to change password. Please try again.");
    }
  };

  // Save Profile Changes
  const handleSave = async () => {
    try {
      setLoading(true);

      const response = await api.post("/user/update", {
        _method: "PUT",
        editProfile: true,
        name: userData.name,
        phone: userData.phone,
        emergency_contact: userData.emergency_contact?.trim() || ""
      });

      setUserData((prevData) => ({
        ...prevData,
        name: response.data.user.name,
        phone: response.data.user.phone,
        emergency_contact: response.data.user.emergency_contact
      }));

      await updateContext();
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile.");
    } finally {
      setLoading(false);
    }
  };

  // Cancel Auto-Renewal
  const handleCancelAutoRenewal = async () => {
    try {
      setLoading(true);
      await api.post("/user/cancel-auto-renewal");
      setUserData((prev) => ({ ...prev, autoRenew: 0 }));
      updateContext();
      toast.success("Auto-renewal cancelled. Your membership will end after expiry.");
    } catch (error) {
      toast.error("Failed to cancel auto-renewal.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen md:py-8">
      {/* Floating Header Card */}
      <div className="max-w-8xl mx-auto sm:px-4">
        <div className="relative bg-white rounded-3xl shadow-xl overflow-hidden mb-12">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-accent/10" />

          {/* Profile Image */}
          <div className="relative flex flex-col items-center pt-12 pb-8">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-accent rounded-full blur opacity-50 group-hover:opacity-75 transition duration-300" />
              <div className="relative">
                <img
                  src={newPhoto || userData.profilePhoto}
                  alt="Profile"
                  className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border-4 border-white shadow-xl object-cover"
                />
                <label
                  htmlFor="photo-upload"
                  className="absolute bottom-2 right-2 bg-white p-3 rounded-full shadow-lg cursor-pointer 
                    transform transition-all duration-300 hover:scale-110 hover:shadow-2xl"
                >
                  <FontAwesomeIcon icon={faCamera} className="text-primary text-xl" />
                  <input
                    id="photo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoChange}
                  />
                </label>
              </div>
            </div>

            <div className="mt-6 text-center">
              <h1 className="text-4xl sm:text-5xl font-bold">
                <span className="text-primary">Welcome, </span>
                <span className="text-accent">{userData.name}</span>
              </h1>
              <div className="mt-2 flex items-center justify-center space-x-2 text-gray-600">
                <FontAwesomeIcon icon={faPaw} className="text-primary animate-bounce" />
                <p className="text-lg">Your Pawsome Profile Dashboard</p>
                <FontAwesomeIcon icon={faPaw} className="text-accent animate-bounce delay-100" />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Membership & Quick Stats */}
          <div className="space-y-8">
            {/* Membership Card */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              <div className="relative h-32 bg-gradient-to-r from-primary to-accent">
                <div className="absolute inset-0 bg-white/10 backdrop-blur-sm" />
                <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2">
                  <div className="bg-white p-4 rounded-full shadow-lg group hover:shadow-2xl transition-all duration-300">
                    <FontAwesomeIcon
                      icon={faCrown}
                      className="text-4xl text-primary group-hover:scale-110 transform transition-all duration-300"
                    />
                  </div>
                </div>
              </div>
              {/* Membership Content */}
              <div className="px-6 pt-16 pb-6">
                {userData.membership ? (
                  <div className="text-center space-y-6">
                    {/* Membership Header */}
                    <div className="space-y-2">
                      <h3 className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                        {userData.membership.name}
                      </h3>
                      <div className="flex items-center justify-center space-x-2">
                        <span className="text-sm text-gray-500">
                          {daysLeft !== null && daysLeft >= 0
                            ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left to expire`
                            : "Expired"}
                        </span>
                        <span className="text-primary">•</span>
                        <span className="text-sm font-semibold text-accent">
                          AED {userData.membership.price}
                        </span>
                      </div>
                      {/* Renew Button */}
                      {(daysLeft !== null && daysLeft <= 7) || daysLeft < 0 ? (
                        <button
                          onClick={() => {
                            window.location.href = routes.membership;
                          }}
                          className="mt-3 px-6 py-2 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold shadow hover:scale-105 transition-all"
                        >
                          {daysLeft < 0 ? "Renew Membership" : "Renew Now"}
                        </button>
                      ) : null}
                    </div>

                    {/* Benefits Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-4 rounded-2xl">
                        <FontAwesomeIcon icon={faPaw} className="text-primary mb-2" />
                        <p className="text-sm text-gray-500">Remaining Free Walks </p>
                        <p className="text-2xl font-bold text-primary">
                          {userData.permission?.free_paw_walk || 0}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-2xl">
                        <FontAwesomeIcon icon={faTaxi} className="text-accent mb-2" />
                        <p className="text-sm text-gray-500">Remaining Free Taxi</p>
                        <p className="text-2xl font-bold text-accent">
                          {userData.permission?.free_paw_taxi || 0}
                        </p>
                      </div>
                    </div>

                    {/* Subscription Status */}
                    <div className="pt-4 border-t">
                      <div className="flex flex-col items-start space-y-3">
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon icon={faCheck} className="text-primary" />
                          <span className="text-sm font-semibold text-primary">Auto-Renewal</span>
                        </div>
                        <span className="text-xs text-gray-500">
                          Next renewal: <span className="font-semibold text-accent">{new Date(userData.membership.end_date).toLocaleDateString()}</span>
                        </span>
                        {userData.autoRenew === 1 && (
                          <div className="w-full bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex flex-col items-start">
                            <span className="text-xs text-yellow-700 mb-2">
                              If you cancel auto-renewal, next month’s payment will not be charged and your membership will end.<br />
                              To continue, you’ll need to re-subscribe after expiry.
                            </span>
                            <div className="w-full flex justify-center mt-4">
                              <button
                                onClick={handleCancelAutoRenewal}
                                className="px-5 py-2 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold shadow hover:scale-105 transition-all text-sm"
                                disabled={loading}
                              >
                                Cancel Auto-Renewal
                              </button>
                            </div>
                          </div>
                        )}
                        {userData.autoRenew === 0 && (
                          <div className="w-full bg-red-50 border border-red-200 rounded-xl p-3">
                            <span className="text-xs text-red-600">
                              Auto-renewal is cancelled. You will need to re-subscribe after your membership ends.
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center space-y-6">
                    <div className="relative inline-block group">
                      <div className="absolute -inset-2 bg-gradient-to-r from-primary to-accent rounded-full blur opacity-25 group-hover:opacity-50 transition duration-300" />
                      <FontAwesomeIcon
                        icon={faPaw}
                        className="relative text-5xl text-primary animate-bounce"
                      />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-primary">Join Our Pack Today!</h3>
                      <p className="text-sm text-gray-600">
                        Get exclusive benefits like free walks, taxi rides, and special discounts
                      </p>
                    </div>
                    <Link
                      to={routes.membership}
                      className="inline-block px-8 py-3 bg-gradient-to-r from-primary to-accent text-white rounded-full 
              font-bold transform transition-all duration-300 hover:scale-105 hover:shadow-lg"
                    >
                      Become a VIP Member
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Profile Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Personal Information */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-primary flex items-center">
                    <FontAwesomeIcon icon={faPaw} className="mr-2" />
                    Personal Information
                  </h3>
                  <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faPaw} className="text-primary" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Read-only fields */}
                  <div className="relative group">
                    <label className="block text-sm font-medium text-gray-600 mb-2">Email</label>
                    <input
                      type="email"
                      value={user.email}
                      disabled
                      className="w-full px-4 py-3 rounded-xl border bg-gray-100 text-gray-400 cursor-not-allowed"
                    />
                    <FontAwesomeIcon
                      icon={faPaw}
                      className="absolute right-4 top-1/2 text-gray-200 transform -translate-y-1/2"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      value={user.username}
                      disabled
                      className="w-full px-4 py-3 rounded-xl border bg-gray-100 text-gray-400 cursor-not-allowed"
                    />
                  </div>

                  {/* Editable fields with animated focus states */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={userData.name}
                      onChange={handleNameChange}
                      className="w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-primary 
                        focus:border-transparent transition-all duration-300"
                      placeholder="Enter your name"
                    />
                  </div>

                  {/* Phone inputs with custom styling */}
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">
                      Phone Number
                    </label>
                    <PhoneInput
                      country={"ae"}
                      value={userData.phone}
                      onChange={handlePhoneChange}
                      containerClass="phone-input-container"
                      inputStyle={{
                        width: "100%",
                        height: "48px",
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        paddingLeft: "48px"
                      }}
                      buttonStyle={{
                        borderRadius: "12px 0 0 12px",
                        border: "none",
                        backgroundColor: "#f8fafc"
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-primary mb-6 flex items-center">
                <FontAwesomeIcon icon={faPhone} className="mr-2" />
                Emergency Contact
              </h3>

              <PhoneInput
                country={"ae"}
                value={userData.emergency_contact}
                onChange={handleEPhoneChange}
                containerClass="phone-input-container"
                inputStyle={{
                  width: "100%",
                  height: "48px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  paddingLeft: "48px"
                }}
                buttonStyle={{
                  borderRadius: "12px 0 0 12px",
                  border: "none",
                  backgroundColor: "#f8fafc"
                }}
              />
            </div>

            {/* Password Section */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="text-xl font-bold text-primary mb-6 flex items-center">
                <FontAwesomeIcon icon={faLock} className="mr-2" />
                Change Password
              </h3>

              <div className="space-y-4">
                {/* Password fields with security indicators */}
                <div>
                  <input
                    type="password"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-primary 
                      focus:border-transparent transition-all duration-300"
                    placeholder="Current Password"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-primary 
                      focus:border-transparent transition-all duration-300"
                    placeholder="New Password"
                  />

                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-primary 
                      focus:border-transparent transition-all duration-300"
                    placeholder="Confirm Password"
                  />
                </div>

                <button
                  onClick={handleChangePassword}
                  className="w-full py-3 bg-gray-100 text-primary rounded-xl font-bold
                    transform transition-all hover:scale-105 hover:shadow-md"
                >
                  <FontAwesomeIcon icon={faLock} className="mr-2" />
                  Update Password
                </button>
              </div>
            </div>

            {/* Account Deletion Section */}
            <AccountDeletionSection />

            {/* Save Button */}
            <button
              onClick={handleSave}
              className="w-full py-4 bg-gradient-to-r from-primary to-accent text-white rounded-xl font-bold text-lg
                transform transition-all duration-300 hover:scale-105 hover:shadow-xl flex items-center justify-center"
            >
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
